/**
 * Material Image Utilities - CONSOLIDATED
 * ========================================
 * UNICO STANDARD: image_main (R2)
 * 
 * Regole:
 * - image_main = URL R2 (UNICA fonte)
 * - NO fallback CDN esterni
 * - NO image_url legacy
 * - Placeholder se image_main non presente
 */

// Placeholder image for materials without image_main
export const PLACEHOLDER_IMAGE = '/placeholder-material.svg';

// R2 public URL base (from env)
const R2_PUBLIC_URL = process.env.REACT_APP_R2_PUBLIC_URL || '';

/**
 * Check if URL is a valid R2 URL
 */
export const isR2Url = (url) => {
  if (!url) return false;
  // R2 URLs should start with the configured public URL
  if (R2_PUBLIC_URL && url.startsWith(R2_PUBLIC_URL)) {
    return true;
  }
  // Common R2 patterns
  return url.includes('.r2.dev') || 
         url.includes('r2.cloudflarestorage.com') ||
         url.includes('.cloudflare');
};

/**
 * Get the image URL for a material
 * 
 * Priority: image_main (normalized R2) > image_url (R2 direct) > placeholder
 * 
 * @param {Object} material - Material object
 * @returns {string} image URL or placeholder
 */
export const getMaterialImageUrl = (material) => {
  if (!material) return PLACEHOLDER_IMAGE;
  
  // Priority: image_main > image_url > placeholder
  if (material.image_main) {
    return material.image_main;
  }
  
  if (material.image_url) {
    return material.image_url;
  }
  
  return PLACEHOLDER_IMAGE;
};

/**
 * Get thumbnail URL for a material
 * Uses image_main (same as main image for now)
 * 
 * @param {Object} material - Material object
 * @returns {string} Thumbnail URL
 */
export const getMaterialThumbnail = (material) => {
  // For now, thumbnail = main image
  // In future, R2 can generate thumbnails on-the-fly
  return getMaterialImageUrl(material);
};

/**
 * Get all images for a material gallery
 * Returns array with image_main as single source
 * Supports R2 image_url as fallback
 * 
 * @param {Object} material - Material object
 * @returns {string[]} Array of image URLs
 */
export const getMaterialImages = (material) => {
  if (!material) return [PLACEHOLDER_IMAGE];
  
  // Priority: image_main (R2 normalized) > image_url (R2 direct) > placeholder
  const mainImage = material.image_main || material.image_url;
  
  if (mainImage) {
    // If images array exists with additional images, include them
    if (Array.isArray(material.images) && material.images.length > 0) {
      const allImages = [mainImage, ...material.images.filter(img => img && img !== mainImage)];
      return allImages;
    }
    return [mainImage];
  }
  
  return [PLACEHOLDER_IMAGE];
};

/**
 * Check if material has a valid image
 * @param {Object} material - Material object
 * @returns {boolean}
 */
export const hasValidImage = (material) => {
  return !!(material?.image_main);
};

/**
 * Check if material needs image migration
 * @param {Object} material - Material object
 * @returns {boolean}
 */
export const needsImageMigration = (material) => {
  if (!material) return false;
  
  // Needs migration if:
  // 1. No image_main
  // 2. Has image_url but not migrated to R2
  return !material.image_main && !!material.image_url;
};

/**
 * Image error handler
 * Use this on <img> onError to show placeholder
 */
export const handleImageError = (event) => {
  event.target.onerror = null; // Prevent infinite loop
  event.target.src = PLACEHOLDER_IMAGE;
};

/**
 * Get image props for a material
 * Returns src and onError handler
 * 
 * @param {Object} material - Material object
 * @param {string} alt - Alt text
 * @returns {Object} Props for img element
 */
export const getImageProps = (material, alt = '') => {
  return {
    src: getMaterialImageUrl(material),
    alt: alt || material?.name || 'Material',
    onError: handleImageError
  };
};

// Stats helper for migration
export const getImageStats = (materials) => {
  if (!materials || !Array.isArray(materials)) {
    return { total: 0, withImage: 0, needsMigration: 0, missing: 0 };
  }
  
  const stats = {
    total: materials.length,
    withImage: 0,
    needsMigration: 0,
    missing: 0
  };
  
  materials.forEach(m => {
    if (m.image_main) {
      stats.withImage++;
    } else if (m.image_url) {
      stats.needsMigration++;
    } else {
      stats.missing++;
    }
  });
  
  return stats;
};

export default {
  getMaterialImageUrl,
  getMaterialThumbnail,
  getMaterialImages,
  hasValidImage,
  needsImageMigration,
  handleImageError,
  getImageProps,
  getImageStats,
  isR2Url,
  PLACEHOLDER_IMAGE
};
